// Rank Momentum dashboard. Answers "are we winning?" at a glance.
//
// Sections (top → bottom):
//   1. Hero KPI strip — current state of every (site, keyword) Spencer
//      has probed, with ▲▼ deltas vs 7/14/28 days ago.
//   2. Bucket distribution — stacked bar showing top-3 / 4-10 / 11-30 /
//      31-100 / not-ranked share.
//   3. Best & worst movers — sites whose avg rank improved/regressed
//      most over 14 days.
//   4. Crossovers — keywords that crossed bucket boundaries:
//      entered top-10 (real wins) vs fell out of top-30 (regressions).
//   5. Owen attribution — what % of Owen's Execute attempts moved
//      rank ≥3 positions in the d14 window.
//
// Refreshes every 60s. Banner at top sets expectations: "you have N
// days of data; trajectory becomes meaningful at day 14."

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { momentumApi, type MomentumResponse, type MomentumCrossover } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function MomentumPage() {
  const q = useQuery({
    queryKey: ["momentum"],
    queryFn: () => momentumApi.read(),
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <div className="text-sm text-ink-500">Loading momentum…</div>;
  if (q.error) return <div className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</div>;
  if (!q.data) return null;

  const m = q.data;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-display font-bold text-ink-900">Rank Momentum</h1>
        <p className="text-sm text-ink-600 mt-1">
          Are we winning? Portfolio-wide rank progress across all Spencer-probed (site, keyword) pairs.
        </p>
      </header>

      <DataDepthBanner data={m} />

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-500 mb-3">Current state</h2>
        <KpiStrip data={m} />
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-500 mb-3">Rank bucket distribution</h2>
        <BucketBar data={m} />
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <MoversTable title="🏆 Best movers (last 14d)" rows={m.movers.best} bestNotWorst />
        <MoversTable title="⚠️ Worst movers (last 14d)" rows={m.movers.worst} bestNotWorst={false} />
      </div>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-500 mb-3">Crossovers (last 14d)</h2>
        <CrossoverBlocks data={m.crossovers} />
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-500 mb-3">Owen attribution</h2>
        <OwenAttributionCard data={m.owenAttribution} />
      </section>
    </div>
  );
}

// ─── Data depth + sample-size banner ─────────────────────────────────

function DataDepthBanner({ data: m }: { data: MomentumResponse }) {
  const days = m.history.depthDays;
  let tone: "amber" | "blue" | "green";
  let headline: string;
  let body: string;
  if (days < 7) {
    tone = "amber";
    headline = `${days}d of probe data — too early to read trajectory.`;
    body = "Google's re-crawl + signal-settle cycle is 7-14 days. Keep Spencer running; trajectory data becomes meaningful around day 14.";
  } else if (days < 21) {
    tone = "blue";
    headline = `${days}d of probe data — early signal forming.`;
    body = "Day 7-21 deltas are directional but noisy. Day 28+ is where you can attribute movement to interventions.";
  } else {
    tone = "green";
    headline = `${days}d of probe data — trajectory is readable.`;
    body = "Sample size is sufficient to attribute rank movement to specific Owen interventions.";
  }
  const palette = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[tone];
  return (
    <div className={`text-sm border rounded-lg px-4 py-3 ${palette}`}>
      <strong>{headline}</strong> {body}
    </div>
  );
}

// ─── KPI strip ───────────────────────────────────────────────────────

function KpiStrip({ data: m }: { data: MomentumResponse }) {
  const cards: KpiCardProps[] = [
    {
      label: "In top-3",
      value: m.current.inTop3,
      d14: m.current.inTop3 - Number(m.past.d14.in_top_3 ?? 0),
      d28: m.current.inTop3 - Number(m.past.d28.in_top_3 ?? 0),
      good: "up",
    },
    {
      label: "In top-10",
      value: m.current.inTop10,
      d14: m.current.inTop10 - Number(m.past.d14.in_top_10 ?? 0),
      d28: m.current.inTop10 - Number(m.past.d28.in_top_10 ?? 0),
      good: "up",
    },
    {
      label: "Push zone (11-30)",
      value: m.current.inPushZone,
      d14: m.current.inPushZone - Number(m.past.d14.in_push_zone ?? 0),
      d28: m.current.inPushZone - Number(m.past.d28.in_push_zone ?? 0),
      good: "up",
    },
    {
      label: "Avg rank",
      value: m.current.avgRank ?? "—",
      d14: m.current.avgRank != null && m.past.d14.avg_rank != null
        ? Math.round((m.current.avgRank - Number(m.past.d14.avg_rank)) * 10) / 10
        : null,
      d28: m.current.avgRank != null && m.past.d28.avg_rank != null
        ? Math.round((m.current.avgRank - Number(m.past.d28.avg_rank)) * 10) / 10
        : null,
      good: "down",
      decimal: 1,
    },
    {
      label: "Competing sites",
      value: m.current.competingSites,
      sublabel: `of ${m.current.totalPairs > 0 ? "30+" : "0"}`,
      d14: null,
      d28: null,
      tooltip: "Sites with at least one keyword in the top-10.",
      good: "up",
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {cards.map((c) => (
        <KpiCard key={c.label} {...c} />
      ))}
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: number | string | null;
  sublabel?: string;
  d14: number | null;
  d28: number | null;
  good: "up" | "down";
  decimal?: number;
  tooltip?: string;
}

function KpiCard({ label, value, sublabel, d14, d28, good, decimal, tooltip }: KpiCardProps) {
  const display = typeof value === "number"
    ? (decimal ? value.toFixed(decimal) : value.toLocaleString())
    : value;
  return (
    <div
      className="bg-white rounded-xl border border-ink-100 p-4 flex flex-col gap-2"
      title={tooltip}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-display font-bold text-ink-900 tabular-nums">{display}</span>
        {sublabel && <span className="text-xs text-ink-500">{sublabel}</span>}
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <DeltaPill label="14d" value={d14} good={good} />
        <DeltaPill label="28d" value={d28} good={good} />
      </div>
    </div>
  );
}

function DeltaPill({ label, value, good }: { label: string; value: number | null; good: "up" | "down" }) {
  if (value == null) {
    return <span className="text-ink-400">{label}: —</span>;
  }
  if (value === 0) {
    return <span className="text-ink-500">{label}: ±0</span>;
  }
  const isPositive = value > 0;
  const isGood = (good === "up" && isPositive) || (good === "down" && !isPositive);
  const arrow = isPositive ? "▲" : "▼";
  const sign = isPositive ? "+" : "";
  const cls = isGood ? "text-emerald-700 font-medium" : "text-red-700 font-medium";
  return <span className={cls}>{label}: {arrow} {sign}{Number.isInteger(value) ? value : value.toFixed(1)}</span>;
}

// ─── Bucket distribution bar ─────────────────────────────────────────

function BucketBar({ data: m }: { data: MomentumResponse }) {
  const total = m.current.totalPairs;
  if (total === 0) {
    return <div className="text-sm text-ink-500">No probes yet.</div>;
  }
  const segments = [
    { label: "Top-3", count: m.current.inTop3, cls: "bg-emerald-600" },
    { label: "4-10", count: m.current.inTop10 - m.current.inTop3, cls: "bg-emerald-400" },
    { label: "11-30 (push)", count: m.current.inPushZone, cls: "bg-blue-500" },
    { label: "31-100", count: m.current.deep, cls: "bg-amber-400" },
    { label: "Not ranked", count: m.current.notRanked, cls: "bg-ink-300" },
  ];
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <div className="flex w-full h-7 rounded overflow-hidden">
        {segments.map((s) => {
          const pct = (s.count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={s.label}
              className={`${s.cls} flex items-center justify-center`}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${s.count} pairs (${pct.toFixed(1)}%)`}
            >
              {pct > 6 && <span className="text-[10px] font-medium text-white">{s.count}</span>}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {segments.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded ${s.cls}`} />
            <span className="text-ink-600">{s.label}</span>
            <span className="text-ink-900 font-medium tabular-nums">{s.count}</span>
            <span className="text-ink-400">({((s.count / total) * 100).toFixed(0)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Movers tables ───────────────────────────────────────────────────

function MoversTable({
  title,
  rows,
  bestNotWorst,
}: {
  title: string;
  rows: MomentumResponse["movers"]["best"];
  bestNotWorst: boolean;
}) {
  type MoverRow = MomentumResponse["movers"]["best"][number];
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<MoverRow>(
    rows,
    {
      site: (r) => r.domain,
      avgDelta: (r) => r.avgDelta,
      pairs: (r) => r.pairs,
      // Up/Down sorts on net (improved - regressed) — most useful single number.
      upDown: (r) => r.improved - r.regressed,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <h3 className="font-semibold mb-3 text-ink-900">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-ink-500 italic">No sites with ≥3 pairs probed both 14d ago and now.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[400px]">
          <thead className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
            <tr>
              <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left pb-2" />
              <SortHeader label="Avg Δ" sortKey="avgDelta" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right pb-2" />
              <SortHeader label="Pairs" sortKey="pairs" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right pb-2" />
              <SortHeader label="Up / Down" sortKey="upDown" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right pb-2" title="Sorted by net (improved minus regressed)." />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.map((r) => (
              <tr key={r.siteId}>
                <td className="py-2">
                  <Link to={`/sites/${r.siteId}`} className="text-accent hover:underline">{r.domain}</Link>
                </td>
                <td className={`py-2 text-right tabular-nums font-mono font-semibold ${
                  r.avgDelta == null ? "text-ink-400"
                  : r.avgDelta < 0 ? "text-emerald-700"
                  : r.avgDelta > 0 ? "text-red-700"
                  : "text-ink-500"
                }`}>
                  {r.avgDelta == null
                    ? "—"
                    : `${r.avgDelta > 0 ? "+" : ""}${r.avgDelta.toFixed(1)}`}
                </td>
                <td className="py-2 text-right tabular-nums text-ink-600">{r.pairs}</td>
                <td className="py-2 text-right tabular-nums text-xs">
                  <span className="text-emerald-700">{r.improved}↑</span>
                  <span className="text-ink-400 mx-1">/</span>
                  <span className="text-red-700">{r.regressed}↓</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <p className="text-[10px] text-ink-400 mt-2">
        {bestNotWorst
          ? "Negative avg Δ = ranks moved up (got better). Sites with at least 3 (site, keyword) pairs probed both 14d ago and today."
          : "Positive avg Δ = ranks dropped. Investigate or shore up content."}
      </p>
    </div>
  );
}

// ─── Crossover blocks ────────────────────────────────────────────────

function CrossoverBlocks({ data: c }: { data: MomentumResponse["crossovers"] }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      <CrossoverList title="🎯 Entered top-3" tone="emerald-strong" rows={c.enteredTop3} />
      <CrossoverList title="🟢 Entered top-10" tone="emerald" rows={c.enteredTop10} />
      <CrossoverList title="🔵 Entered push zone (11-30)" tone="blue" rows={c.enteredPushZone} />
      <CrossoverList title="🔴 Fell out of top-10" tone="red" rows={c.fellOutTop10} />
      <CrossoverList title="🟠 Fell out of top-30" tone="amber" rows={c.fellOutTop30} />
    </div>
  );
}

function CrossoverList({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: "emerald-strong" | "emerald" | "blue" | "amber" | "red";
  rows: MomentumCrossover[];
}) {
  const palette = {
    "emerald-strong": "border-emerald-300 bg-emerald-50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    blue: "border-blue-200 bg-blue-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    red: "border-red-200 bg-red-50/50",
  }[tone];
  return (
    <div className={`rounded-xl border ${palette} p-4`}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-700 mb-2">
        {title} <span className="text-ink-400">({rows.length})</span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-ink-400 italic">none in window</p>
      ) : (
        <ul className="space-y-1.5 text-xs">
          {rows.slice(0, 8).map((r, i) => (
            <li key={`${r.site_id}-${r.keyword}-${i}`} className="flex justify-between gap-2">
              <Link
                to={`/sites/${r.site_id}`}
                className="text-ink-700 hover:text-accent truncate"
                title={`${r.domain} · "${r.keyword}"`}
              >
                <span className="text-ink-500">{r.domain}:</span> {r.keyword}
              </Link>
              <span className="text-ink-500 font-mono tabular-nums whitespace-nowrap">
                {r.from_pos != null ? `#${r.from_pos}` : "n/r"}{" → "}{r.to_pos != null ? `#${r.to_pos}` : "n/r"}
              </span>
            </li>
          ))}
          {rows.length > 8 && <li className="text-[10px] text-ink-400 italic">+{rows.length - 8} more</li>}
        </ul>
      )}
    </div>
  );
}

// ─── Owen attribution ────────────────────────────────────────────────

function OwenAttributionCard({ data: o }: { data: MomentumResponse["owenAttribution"] }) {
  if (o.totalAttempts === 0) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 text-sm text-ink-500">
        No Owen Execute attempts on file yet. Click Execute on content_brief alerts in the Inbox to start the loop.
      </div>
    );
  }
  const hitRate = o.totalAttempts > 0 ? (o.moved3plus / o.totalAttempts) * 100 : 0;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Attempts" value={o.totalAttempts} sublabel="Owen → Execute → Grace" />
        <Stat
          label="Hit rate"
          value={`${hitRate.toFixed(0)}%`}
          sublabel={`${o.moved3plus}/${o.totalAttempts} moved ≥3 ranks`}
          tone={hitRate >= 50 ? "good" : hitRate > 0 ? "neutral" : "warn"}
        />
        <Stat
          label="Avg pre-rank"
          value={o.avgPre != null ? `#${o.avgPre.toFixed(1)}` : "—"}
          sublabel="when brief was filed"
        />
        <Stat
          label="Avg d14 rank"
          value={o.avgPost14 != null ? `#${o.avgPost14.toFixed(1)}` : "—"}
          sublabel="post-Execute capture"
          tone={
            o.avgPre != null && o.avgPost14 != null
              ? o.avgPost14 < o.avgPre ? "good"
              : o.avgPost14 > o.avgPre ? "warn"
              : "neutral"
              : undefined
          }
        />
      </div>
      {o.medianDelta14 != null && (
        <p className="mt-4 text-xs text-ink-600">
          <strong>Median rank delta on Owen-touched keywords (d14):</strong>{" "}
          <span className={
            o.medianDelta14 < 0 ? "text-emerald-700 font-semibold"
            : o.medianDelta14 > 0 ? "text-red-700 font-semibold"
            : "text-ink-500"
          }>
            {o.medianDelta14 > 0 ? "+" : ""}{o.medianDelta14.toFixed(1)}
          </span>
          {" "}positions {o.medianDelta14 < 0 ? "(improved)" : o.medianDelta14 > 0 ? "(regressed)" : "(flat)"}.
          {o.totalAttempts < 10 && <span className="text-ink-500"> Sample size still small — pattern firms up after ~10 attempts.</span>}
        </p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sublabel,
  tone,
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  tone?: "good" | "warn" | "neutral";
}) {
  const cls = tone === "good" ? "text-emerald-700"
    : tone === "warn" ? "text-red-700"
    : "text-ink-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`text-2xl font-display font-bold tabular-nums ${cls}`}>{value}</div>
      {sublabel && <div className="text-[10px] text-ink-500 mt-0.5">{sublabel}</div>}
    </div>
  );
}
